import { Divider, Form } from 'antd'
import { usePricingSchemasTranslation } from 'hooks/translation/usePricingSchemaTranslation'
import { useEffect } from 'react'
import { CreatePricingModalWrapper } from '../../../atom/price'
import { usePricingSchemas } from '../../../hooks/usePricingSchemas'
import { PricingSchema } from '../../../models/price'
import { Tenant } from '../../../models/tenant'
import { ChargingSectionModal } from './charging-section'
import { ConstantsSectionModal } from './constants-section'
import { FooterSectionModal } from './footer-section'
import { LoiteringSectionModal } from './loitering-section'
import { NameSectionModal } from './name-section'
import { DiscountSchema } from 'models/discount'

interface props {
  tenant: Tenant
  selectedPricingSchema: PricingSchema
  disableChargerId?: boolean
  onCancel: () => void
  onOk: (goToCreate?: boolean) => void
  hideMaxLimit?: boolean
  selectedDiscountSchemas?: DiscountSchema[]
  discounts?: DiscountSchema[]
  setSelectedDiscountSchemas: (existingDiscounts: DiscountSchema[]) => void
  setLoadedPricingSchema?: (pricingSchema: PricingSchema) => void
}

const EditPricingSchemaSection: React.FC<props> = ({
  tenant,
  disableChargerId = false,
  selectedPricingSchema,
  onCancel,
  onOk,
  hideMaxLimit = false,
  selectedDiscountSchemas = [],
  discounts,
  setSelectedDiscountSchemas,
  setLoadedPricingSchema,
}) => {
  const [form] = Form.useForm()
  const {
    loading,
    initialValues,
    nameExists,
    eventSchedules,
    timeOfDay,
    timeOfDayLoitering,
    enableCurtailmentAdjustment,
    loiteringType,
    allListings,
    pricingType,
    listings,
    setLoiteringType,
    setEnableCurtailmentAdjustment,
    setTimeOfDay,
    setTimeOfDayLoitering,
    getPricingNames,
    nameCheck,
    setPricePerUnitLoitering,
    setPricePerUnit,
    onFinish,
    setTimeOfDayPricingWeekday,
    setTimeOfDayPricingWeekend,
    setTimeOfDayPricingWeekDayLoitering,
    setTimeOfDayPricingWeekendLoitering,
    setListings,
    setAllListings,
    setPricingType,
    nextInModal,
    setNextInModal,
  } = usePricingSchemas(tenant, onOk, onCancel, selectedPricingSchema, setLoadedPricingSchema)

  useEffect(() => {
    getPricingNames(Number(tenant.id))
    const pricingArr = Array.from({ length: 48 }, () => Number(initialValues.dollar_rate) * 100)
    setTimeOfDayPricingWeekday(pricingArr)
    setTimeOfDayPricingWeekend(pricingArr)
    const pricingLoiteringArr = Array.from({ length: 48 }, () => Number(initialValues.loitering_dollar_rate) * 100)
    setTimeOfDayPricingWeekDayLoitering(pricingLoiteringArr)
    setTimeOfDayPricingWeekendLoitering(pricingLoiteringArr)
  }, [])

  const { loiteringText } = usePricingSchemasTranslation()

  useEffect(() => {
    if (nextInModal) {
      form.submit()
    }
  }, [nextInModal])

  useEffect(() => {
    if (discounts && listings) {
      let existingDiscounts: DiscountSchema[] = []
      discounts.forEach((discount) => {
        if (
          discount?.appliedListings?.some((appliedListing) =>
            listings.some((listing) => listing.id === appliedListing.listingId),
          )
        ) {
          existingDiscounts.push(discount)
        }
      })
      setSelectedDiscountSchemas(existingDiscounts)
    }
  }, [discounts, listings])
  return (
    <CreatePricingModalWrapper>
      <Form
        colon={false}
        form={form}
        requiredMark={true}
        labelAlign="left"
        initialValues={initialValues}
        onFinish={(values) => {
          let missedOutValues = {}
          if (hideMaxLimit) {
            missedOutValues = {
              loitering_maximum: initialValues.loitering_maximum,
              payment_limit: initialValues.payment_limit,
            }
          }
          onFinish(
            { ...values, ...missedOutValues },
            'editPricingSchema',
            listings,
            !!selectedDiscountSchemas.length || nextInModal,
          )
        }}
        labelWrap={true}
      >
        <NameSectionModal
          tenant={tenant}
          listings={listings}
          selectedPricingSchema={selectedPricingSchema}
          nameExists={nameExists}
          allListings={allListings}
          type="edit"
          nameCheck={nameCheck}
          getPricingNames={getPricingNames}
          setListings={setListings}
          setAllListings={setAllListings}
          disableChargerId={disableChargerId}
        />
        <ChargingSectionModal
          form={form}
          isCanadaSite={tenant?.location?.country === 'CA' || tenant?.country === 'CA'}
          timeOfDay={timeOfDay}
          eventSchedules={eventSchedules}
          enableCurtailmentAdjustment={enableCurtailmentAdjustment}
          priceType={pricingType}
          setPricePerUnit={setPricePerUnit}
          setTimeOfDay={setTimeOfDay}
          setEnableCurtailmentAdjustment={setEnableCurtailmentAdjustment}
          setPriceType={setPricingType}
          hideMaxLimit={hideMaxLimit}
        />

        <Divider>
          <span className="paragraph-02-bold">{loiteringText}</span>
        </Divider>

        <LoiteringSectionModal
          form={form}
          loiteringType={loiteringType}
          timeOfDayLoitering={timeOfDayLoitering}
          eventSchedules={eventSchedules}
          setPricePerUnitLoitering={setPricePerUnitLoitering}
          setTimeOfDayLoitering={setTimeOfDayLoitering}
          setLoiteringType={setLoiteringType}
          hideMaxLimit={hideMaxLimit}
        />
        <ConstantsSectionModal hideMaxLimit={hideMaxLimit} />

        <FooterSectionModal
          loading={loading}
          form={form}
          nameExists={nameExists}
          type="edit"
          onCancel={onCancel}
          setNextInModal={setNextInModal}
          hasDiscountPlan={!!selectedDiscountSchemas.length}
        />
      </Form>
    </CreatePricingModalWrapper>
  )
}

export default EditPricingSchemaSection
